import { useMemo, useState } from "react";
import { ArrowRight, ChevronRight, LoaderCircle } from "lucide-react";
import { Link } from "react-router";
import type { Allocation } from "../../../api/SolarCloudApi/models/Allocation";
import type { EnergyCompany } from "../../../api/SolarCloudApi/models/EnergyCompany";
import { Button } from "../../../components/ui/Button";
import { Card, CardContent } from "../../../components/ui/Card";
import { useEnergyCompanies } from "../../../queries/useEnergyCompanies";
import {
  getKilowattsFromWatts,
  getLinkedElectricityAllocations,
  maskCustomerReferenceNumber,
} from "./bottomDashboardCards";

const energyLogoAssets = import.meta.glob<string>(
  "/src/res/logos/energy-logos/*.{svg,png,jpg,jpeg}",
  { eager: true, import: "default", query: "?url" }
);

const energyLogoUrls = new Map<string, string>();
Object.entries(energyLogoAssets)
  .sort(([firstPath], [secondPath]) => {
    const firstIsSvg = firstPath.toLowerCase().endsWith(".svg");
    const secondIsSvg = secondPath.toLowerCase().endsWith(".svg");
    return Number(secondIsSvg) - Number(firstIsSvg);
  })
  .forEach(([assetPath, assetUrl]) => {
    const filename = assetPath
      .slice(assetPath.lastIndexOf("/") + 1)
      .replace(/\.[^.]+$/, "");
    const normalizedFilename = normalizeProviderName(filename);

    if (!energyLogoUrls.has(normalizedFilename)) {
      energyLogoUrls.set(normalizedFilename, assetUrl);
    }
  });

type LinkedAccountsCardProps = {
  allocations?: Allocation[];
  isLoading: boolean;
  error?: Error | null;
  onRetry: () => void;
};

export default function LinkedAccountsCard({
  allocations,
  isLoading,
  error,
  onRetry,
}: LinkedAccountsCardProps) {
  const linkedAllocations = useMemo(
    () => getLinkedElectricityAllocations(allocations),
    [allocations]
  );
  const displayedAllocations = linkedAllocations.slice(0, 2);
  const needsCompanyLookup = displayedAllocations.some((allocation) => {
    const payee = allocation.energyCompanyPayee;
    const companyName = payee?.energyCompany?.name?.trim();
    return !companyName && Boolean(payee?.energyCompany?.uuid) && !payee?.otherName?.trim();
  });
  const {
    data: energyCompanies,
    isError: energyCompaniesError,
    refetch: refetchEnergyCompanies,
  } = useEnergyCompanies({
    queryConfig: { enabled: needsCompanyLookup },
  });

  return (
    <Card
      aria-busy={isLoading}
      className="h-full rounded-[20px] border-[#232836] bg-[#12151C] py-0 text-[#D0D0D0] shadow-none"
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-[13px] font-semibold text-[#D0D0D0]">
            Linked Energy Accounts
          </h2>
          <Link
            className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-[#E9A837] hover:text-[#FFC64D]"
            to="/dashboard/allocations/electricity"
          >
            View all <ArrowRight aria-hidden="true" className="size-3" />
          </Link>
        </div>

        {isLoading && allocations === undefined ? (
          <div
            className="flex min-h-[92px] items-center justify-center gap-2 text-xs text-[#9CA3AF]"
            role="status"
          >
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
            Loading linked accounts…
          </div>
        ) : error && allocations === undefined ? (
          <div className="flex min-h-[92px] flex-col items-center justify-center gap-2 px-4 text-center" role="alert">
            <p className="text-xs text-[#9CA3AF]">
              Unable to load linked electricity accounts.
            </p>
            <RetryButton onClick={onRetry} />
          </div>
        ) : allocations === undefined ? (
          <div className="flex min-h-[92px] items-center justify-center px-4 text-center text-xs text-[#9CA3AF]" role="status">
            Account details are unavailable.
          </div>
        ) : linkedAllocations.length === 0 ? (
          <div className="flex min-h-[92px] flex-col items-center justify-center gap-2 px-4 text-center">
            <p className="text-xs text-[#9CA3AF]">
              No electricity accounts are linked yet.
            </p>
            <Link
              className="text-xs font-semibold text-[#E9A837] hover:text-[#FFC64D]"
              to="/dashboard/allocations/electricity"
            >
              Link an account
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div className="mt-3 flex items-center justify-between gap-2" role="status">
                <p className="text-xs text-amber-300">
                  Showing the last loaded account details; the latest refresh failed.
                </p>
                <RetryButton onClick={onRetry} />
              </div>
            )}
            {energyCompaniesError && (
              <div className="mt-3 flex items-center justify-between gap-2" role="status">
                <p className="text-xs text-amber-300">
                  Some provider names are unavailable.
                </p>
                <RetryButton onClick={() => void refetchEnergyCompanies()} />
              </div>
            )}
            <ul className="mt-2 divide-y divide-[#232836]">
              {displayedAllocations.map((allocation, index) => {
                const company = allocation.energyCompanyPayee?.energyCompany;
                const catalogCompany = energyCompanies?.find(
                  (candidate) => candidate.uuid === company?.uuid
                );
                const providerName = getProviderName(
                  company,
                  catalogCompany,
                  allocation.energyCompanyPayee?.otherName
                );
                const capacityKw = getKilowattsFromWatts(allocation.wattCapacity);
                const referenceNumber = maskCustomerReferenceNumber(
                  allocation.energyCompanyPayee?.customerReferenceNumber
                );
                const referenceEnding = referenceNumber
                  ?.slice(-4)
                  .replace(/^•+/, "");
                const referenceLabel = referenceNumber
                  ? `CRN ending ...${referenceEnding || "••••"}`
                  : "CRN not provided";

                return (
                  <li
                    className="py-2 first:pt-1 last:pb-0"
                    key={allocation.uuid ?? `${providerName}-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      <CompanyLogo providerName={providerName} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-xs font-medium text-[#D0D0D0]">
                            {providerName}
                          </p>
                          <p className="shrink-0 text-sm font-semibold text-[#D0D0D0]">
                            {formatKilowatts(capacityKw)}
                          </p>
                        </div>
                        <p className="mt-0.5 truncate text-[10px] text-[#9CA3AF]">
                          {referenceLabel}
                        </p>
                        <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-[#76AF26]">
                          <span aria-hidden="true" className="size-1.5 rounded-full bg-[#76AF26]" />
                          Active
                        </span>
                      </div>
                      <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-[#87909F]" />
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function CompanyLogo({ providerName }: { providerName: string }) {
  const logoUrl = getCompanyLogoUrl(providerName);

  return (
    <CompanyLogoWithState
      key={JSON.stringify([providerName, logoUrl ?? null])}
      logoUrl={logoUrl}
      providerName={providerName}
    />
  );
}

export function getCompanyLogoUrl(
  providerName: string
): string | undefined {
  return energyLogoUrls.get(
    normalizeProviderName(providerName)
  );
}

function CompanyLogoWithState({
  providerName,
  logoUrl,
}: {
  providerName: string;
  logoUrl: string | undefined;
}) {
  const [hasError, setHasError] = useState(false);

  return (
    <CompanyLogoContents
      hasError={hasError}
      logoUrl={logoUrl}
      onImageError={() => setHasError(true)}
      providerName={providerName}
    />
  );
}

export function CompanyLogoContents({
  providerName,
  logoUrl,
  hasError,
  onImageError,
}: {
  providerName: string;
  logoUrl: string | undefined;
  hasError: boolean;
  onImageError: () => void;
}) {
  return (
    <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#2F3648] bg-[#1A1E28] text-[10px] font-semibold text-[#D0D0D0]">
      {logoUrl && !hasError ? (
        <img
          alt={`${providerName} logo`}
          className="size-full object-contain p-1"
          onError={onImageError}
          src={logoUrl}
        />
      ) : (
        <span aria-hidden="true">{getProviderInitials(providerName)}</span>
      )}
    </div>
  );
}

function getProviderInitials(providerName: string): string {
  const words = providerName.trim().split(/\s+/).filter(Boolean);
  return words.length > 0
    ? words.slice(0, 2).map((word) => word[0]).join("").toUpperCase()
    : "EP";
}

function normalizeProviderName(providerName: string): string {
  return providerName.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      className="h-7 shrink-0 rounded-lg border border-[#2F3648] px-2 text-xs text-[#D0D0D0] hover:bg-[#181C25]"
      onClick={onClick}
      type="button"
      variant="outline"
    >
      Retry
    </Button>
  );
}

function getProviderName(
  company: EnergyCompany | undefined,
  catalogCompany: EnergyCompany | undefined,
  otherName: string | undefined
): string {
  const companyName = company?.name?.trim() || catalogCompany?.name?.trim();
  if (companyName?.toLowerCase() === "other") {
    return otherName?.trim() || "Other provider";
  }

  return companyName || otherName?.trim() || "Energy provider";
}

function formatKilowatts(value: number | undefined): string {
  if (value === undefined) return "—";

  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value)} kW`;
}
